import Button from '@gaia/components/Button';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {popupZIndex} from '@gaia/utils/gaia/zIndices';
import {Tooltip, type TooltipProps, Typography} from '@mui/material';
import omit from 'lodash/omit';
import {useContext} from 'react';

import AuthModalContext from './contexts/AuthModalContext';

export type SubscriptionTooltipProps = Partial<TooltipProps> &
    (
        | {
              subscription: 'user' | 'member' | 'gaiagreen' | 'gaiafree';
              analyticsContext: string;
              featureType?: string;
              children: TooltipProps['children'];
          }
        | {
              subscription: undefined;
              analyticsContext?: string;
              featureType?: string;
              children: TooltipProps['children'];
          }
    );

export default function SubscriptionTooltip({
    featureType,
    subscription,
    ...props
}: SubscriptionTooltipProps) {
    const {isAuthenticated, hasPremiumSubscription} = useUser();
    const label = subscription && (featureType || 'feature');

    if (subscription === 'user' && !isAuthenticated) {
        return (
            <SubscriptionTooltipInner
                title={props.title ?? ''}
                subscriptionHref='/register/'
                subscriptionTitle={`Sign up for a free account to use this ${label}`}
                isAuthenticated
                {...props}
            />
        );
    } else if (
        (subscription === 'gaiagreen' || subscription === 'member') && //'member is a sunset membership level'
        !hasPremiumSubscription
    ) {
        return (
            <SubscriptionTooltipInner
                title={props.title ?? ''}
                subscriptionHref='/premium/'
                subscriptionTitle={`Become a Premium Member to use this ${label}`}
                isAuthenticated
                {...props}
            />
        );
    } else if (props.title) {
        return (
            <Tooltip
                title={<div style={{padding: theme.spacing(1)}}>{props.title}</div>}
                {...omit({...props}, 'title', 'analyticsContext')}
            />
        );
    } else {
        return props.children;
    }
}

interface SubscriptionTooltipInnerProps extends TooltipProps {
    subscriptionTitle: string;
    subscriptionHref: string;
    analyticsContext?: string;
    isAuthenticated: boolean;
}

function SubscriptionTooltipInner({
    subscriptionTitle,
    subscriptionHref,
    title,
    analyticsContext,
    isAuthenticated,
    ...restProps
}: SubscriptionTooltipInnerProps) {
    const setAuthModalState = useContext(AuthModalContext);
    const onUserAuthenticated = () => setAuthModalState?.({type: undefined, analyticsContext});

    return (
        <Tooltip
            {...restProps}
            componentsProps={{
                popper: {sx: {zIndex: popupZIndex + 1}},
            }}
            leaveDelay={100}
            title={
                <Typography
                    component='div'
                    variant='body1'
                    color='inherit'
                    style={{padding: theme.spacing(1)}}
                >
                    <div style={{fontWeight: 'bold'}}>{title}</div>

                    {subscriptionTitle}
                    <div style={{marginTop: theme.spacing(2)}}>
                        {isAuthenticated ? (
                            <Button
                                appearance='primary'
                                href={`${subscriptionHref}?next=${window.location.pathname}`}
                                onClick={() =>
                                    ampli.subscriptionTooltipUpgrade({
                                        category: 'map2',
                                        context: analyticsContext,
                                    })
                                }
                            >
                                Upgrade
                            </Button>
                        ) : (
                            <>
                                <Button
                                    size='small'
                                    style={{
                                        marginRight: theme.spacing(2),
                                    }}
                                    onClick={() => {
                                        ampli.subscriptionTooltipLogIn({
                                            category: 'map2',
                                            context: analyticsContext,
                                        });
                                        setAuthModalState?.({
                                            type: 'login',
                                            onUserAuthenticated,
                                            analyticsContext,
                                        });
                                    }}
                                >
                                    Log In
                                </Button>
                                <Button
                                    appearance='primary'
                                    size='small'
                                    onClick={() => {
                                        ampli.subscriptionTooltipSignUp({
                                            category: 'map2',
                                            context: analyticsContext,
                                        });
                                        setAuthModalState?.({
                                            type: 'register',
                                            onUserAuthenticated,
                                            analyticsContext,
                                        });
                                    }}
                                >
                                    Sign Up
                                </Button>
                            </>
                        )}
                    </div>
                </Typography>
            }
        />
    );
}
